/**
 * Database Configuration
 * Environment-specific configuration for different data sources
 */

export interface DatabaseConfig {
  type: 'mongodb' | 'realm' | 'mock';
  mongodb: {
    connectionString: string;
    database: string;
    apiBaseUrl: string;
    directConnection?: boolean;
    connectTimeoutMS?: number;
    serverSelectionTimeoutMS?: number;
  };
  realm: {
    path?: string;
    schemaVersion: number;
  };
  sync: {
    enabled: boolean;
    interval: number; // in milliseconds
  };
}

// Environment Detection - Use Vite's import.meta.env instead of process.env
const isProduction = import.meta.env.MODE === 'production';
const isDevelopment = import.meta.env.MODE === 'development';
const isTest = import.meta.env.MODE === 'test';

// Environment-specific configurations
const environments = {
  // LOCAL DEVELOPMENT - MongoDB database
  local: {
    type: 'mongodb' as const, // Changed back from 'mock' to 'mongodb'
    mongodb: {
      connectionString: import.meta.env.VITE_MONGODB_LOCAL_URL || 'mongodb://localhost:27047/cashierapp?directConnection=true',
      database: import.meta.env.VITE_MONGODB_LOCAL_DATABASE || 'cashierapp',
      apiBaseUrl:
        import.meta.env.VITE_API_BASE_URL ||
        import.meta.env.VITE_API_LOCAL_BASE_URL ||
        'http://localhost:8080',
      directConnection: true,
      connectTimeoutMS: 10000,
      serverSelectionTimeoutMS: 5000,
    },
    realm: {
      path: './incashflow-local.realm',
      schemaVersion: 1,
    },
    sync: {
      enabled: true, // Enable sync for offline support
      interval: 30000,
    },
  },

  // STAGING - Remote database for testing
  staging: {
    type: 'mongodb' as const,
    mongodb: {
      connectionString: import.meta.env.VITE_MONGODB_STAGING_URL || 'mongodb://104.248.95.38:27027/cashierapp?directConnection=true',
      database: import.meta.env.VITE_MONGODB_STAGING_DATABASE || 'cashierapp',
      apiBaseUrl:
        import.meta.env.VITE_API_BASE_URL ||
        import.meta.env.VITE_API_STAGING_BASE_URL ||
        'https://incashflow.inrits.tech',
      directConnection: true,
      connectTimeoutMS: 15000,
      serverSelectionTimeoutMS: 10000,
    },
    realm: {
      path: './incashflow-staging.realm',
      schemaVersion: 1,
    },
    sync: {
      enabled: true,
      interval: 30000, // 30 seconds
    },
  },

  // PRODUCTION - Production database
  production: {
    type: 'mongodb' as const,
    mongodb: {
      connectionString: import.meta.env.VITE_MONGODB_PROD_URL || 'mongodb://104.248.95.38:27027/cashierapp?directConnection=true',
      database: import.meta.env.VITE_MONGODB_PROD_DATABASE || 'cashierapp',
      apiBaseUrl:
        import.meta.env.VITE_API_BASE_URL ||
        import.meta.env.VITE_API_PROD_BASE_URL ||
        'https://incashflow.inrits.tech',
      directConnection: true,
      connectTimeoutMS: 20000,
      serverSelectionTimeoutMS: 15000,
    },
    realm: {
      path: './incashflow-production.realm',
      schemaVersion: 1,
    },
    sync: {
      enabled: true,
      interval: 30000, // 30 seconds
    },
  },

  // MOCK - For testing without database
  mock: {
    type: 'mock' as const,
    mongodb: {
      connectionString: '',
      database: 'mock',
      apiBaseUrl: 'http://localhost:3000',
      directConnection: false,
    },
    realm: {
      path: ':memory:',
      schemaVersion: 1,
    },
    sync: {
      enabled: false,
      interval: 0,
    },
  },
};

/**
 * Get database configuration based on environment
 */
export function getDatabaseConfig(): DatabaseConfig {
  const viteMode = import.meta.env.MODE;
  const databaseEnv = import.meta.env.VITE_DATABASE_ENVIRONMENT;
  const useRemoteDb = import.meta.env.VITE_USE_REMOTE_DATABASE === 'true';

  let selectedEnvironment: keyof typeof environments;

  // Local Vite dev modes always target the local API — never staging/production.
  if (viteMode === 'localdev' || viteMode === 'development') {
    selectedEnvironment = 'local';
    console.log(`🏠 Using local database (${viteMode} mode): ${selectedEnvironment}`);
  } else if (databaseEnv && databaseEnv in environments) {
    selectedEnvironment = databaseEnv as keyof typeof environments;
    console.log(`🔧 Using explicit database environment: ${selectedEnvironment}`);
  } else if (useRemoteDb) {
    // Use remote database
    selectedEnvironment = isProduction ? 'production' : 'staging';
    console.log(`🌐 Using remote database: ${selectedEnvironment}`);
  } else {
    // Use local database for development
    selectedEnvironment = 'local';
    console.log(`🏠 Using local database: ${selectedEnvironment}`);
  }
  
  const config = environments[selectedEnvironment];
  
  console.log(`📊 Database Configuration:`, {
    environment: selectedEnvironment,
    type: config.type,
    database: config.mongodb.database,
    connectionString: config.mongodb.connectionString.replace(/\/\/.*@/, '//***:***@'), // Hide credentials
    syncEnabled: config.sync.enabled
  });
  
  return config;
}

/**
 * Get environment-specific configuration
 */
export function getEnvironmentConfig() {
  return {
    isDevelopment,
    isProduction,
    isTest,
    mode: import.meta.env.MODE,
    databaseEnvironment: import.meta.env.VITE_DATABASE_ENVIRONMENT,
    useRemoteDatabase: import.meta.env.VITE_USE_REMOTE_DATABASE === 'true',
  };
}

/**
 * Validate database configuration
 */
export function validateDatabaseConfig(config: DatabaseConfig): boolean {
  if (!config) {
    console.error('❌ Database config is missing');
    return false;
  }
  
  if (config.type === 'mongodb') {
    if (!config.mongodb.connectionString) {
      console.error('❌ MongoDB connection string is missing');
      return false;
    }
    
    if (!config.mongodb.database) {
      console.error('❌ MongoDB database name is missing');
      return false;
    }
  }
  
  return true;
}

// Export default configuration
export const databaseConfig = getDatabaseConfig();